/**
 * Ontology — shared knowledge graph for the FlowState ecosystem.
 *
 * Provides a runtime registry and canonical Zod schemas for concepts,
 * relations, and assertions. Intended to be the source of truth for:
 *   - Agent reasoning (what is a task, deal, principal, etc.)
 *   - Domain-driven memory retrieval (scope via ontology)
 *   - Mycelium event routing (domain concepts mirror EventDomain values)
 *
 * @module ontology
 * @since 2026-07-10
 */

import { z } from 'zod'
import { MemoryScopeKindSchema } from './domains/memory.events.ts'
import { EventDomainSchema } from './types/backbone.ts'
import { AgentSurfaces, DEFAULT_AGENT_CLIS } from './agent-registry.ts'

// ── Enums ─────────────────────────────────────────────────────────────────

export const OntologyConceptKindSchema = z.enum([
  'entity',
  'action',
  'attribute',
  'relationship',
  'event',
  'domain',
  'scope',
  'surface',
])
export type OntologyConceptKind = z.infer<typeof OntologyConceptKindSchema>

export const OntologyRelationCardinalitySchema = z.enum([
  'one-to-one',
  'one-to-many',
  'many-to-one',
  'many-to-many',
])
export type OntologyRelationCardinality = z.infer<typeof OntologyRelationCardinalitySchema>

// ── Core schemas ───────────────────────────────────────────────────────────

export const OntologyConceptSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().optional(),
  kind: OntologyConceptKindSchema,
  domain: z.string().optional(),
  synonyms: z.array(z.string()).default([]),
  metadata: z.record(z.unknown()).default({}),
  principal_id: z.string().uuid().nullable().optional(),
  scope_kind: z.string().optional(),
  scope_id: z.string().uuid().nullable().optional(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
  source: z.string().optional(),
})
export type OntologyConcept = z.infer<typeof OntologyConceptSchema>

export const OntologyRelationSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().optional(),
  source_concept_slugs: z.array(z.string()).default([]),
  target_concept_slugs: z.array(z.string()).default([]),
  cardinality: OntologyRelationCardinalitySchema.optional(),
  is_directed: z.boolean().default(true),
  metadata: z.record(z.unknown()).default({}),
  principal_id: z.string().uuid().nullable().optional(),
  scope_kind: z.string().optional(),
  scope_id: z.string().uuid().nullable().optional(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
  source: z.string().optional(),
})
export type OntologyRelation = z.infer<typeof OntologyRelationSchema>

export const OntologyAssertionSchema = z.object({
  id: z.string().uuid(),
  subject_concept_id: z.string().uuid(),
  relation_id: z.string().uuid(),
  object_concept_id: z.string().uuid().nullable().optional(),
  value: z.string().nullable().optional(),
  confidence: z.number().min(0).max(1).default(1.0),
  evidence: z.string().optional(),
  scope_kind: z.string().optional(),
  scope_id: z.string().uuid().nullable().optional(),
  principal_id: z.string().uuid().nullable().optional(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
  source: z.string().optional(),
})
export type OntologyAssertion = z.infer<typeof OntologyAssertionSchema>

// ── Registry ───────────────────────────────────────────────────────────────

export interface OntologySnapshot {
  concepts: OntologyConcept[]
  relations: OntologyRelation[]
  assertions: OntologyAssertion[]
}

function newId(): string {
  if (typeof globalThis !== 'undefined' && 'crypto' in globalThis && typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function nowIso(): string {
  return new Date().toISOString()
}

export class OntologyRegistry {
  private concepts = new Map<string, OntologyConcept>()
  private relations = new Map<string, OntologyRelation>()
  private assertions = new Map<string, OntologyAssertion>()

  defineConcept(input: Omit<z.input<typeof OntologyConceptSchema>, 'id' | 'created_at' | 'updated_at'>): OntologyConcept {
    const concept: OntologyConcept = {
      ...input,
      synonyms: input.synonyms ?? [],
      metadata: input.metadata ?? {},
      id: newId(),
      created_at: nowIso(),
      updated_at: nowIso(),
    }
    this.concepts.set(concept.slug, concept)
    return concept
  }

  getConcept(slug: string): OntologyConcept | undefined {
    return this.concepts.get(slug)
  }

  getConceptById(id: string): OntologyConcept | undefined {
    return Array.from(this.concepts.values()).find((c) => c.id === id)
  }

  getConcepts(): OntologyConcept[] {
    return Array.from(this.concepts.values())
  }

  getConceptsByDomain(domain: string): OntologyConcept[] {
    return this.getConcepts().filter((c) => c.domain === domain)
  }

  getConceptsByKind(kind: OntologyConceptKind): OntologyConcept[] {
    return this.getConcepts().filter((c) => c.kind === kind)
  }

  defineRelation(input: Omit<z.input<typeof OntologyRelationSchema>, 'id' | 'created_at' | 'updated_at'>): OntologyRelation {
    const relation: OntologyRelation = {
      ...input,
      source_concept_slugs: input.source_concept_slugs ?? [],
      target_concept_slugs: input.target_concept_slugs ?? [],
      is_directed: input.is_directed ?? true,
      metadata: input.metadata ?? {},
      id: newId(),
      created_at: nowIso(),
      updated_at: nowIso(),
    }
    this.relations.set(relation.slug, relation)
    return relation
  }

  getRelation(slug: string): OntologyRelation | undefined {
    return this.relations.get(slug)
  }

  getRelationById(id: string): OntologyRelation | undefined {
    return Array.from(this.relations.values()).find((r) => r.id === id)
  }

  getRelations(): OntologyRelation[] {
    return Array.from(this.relations.values())
  }

  assert(input: Omit<z.input<typeof OntologyAssertionSchema>, 'id' | 'created_at' | 'updated_at'>): OntologyAssertion {
    const assertion: OntologyAssertion = {
      ...input,
      confidence: input.confidence ?? 1.0,
      id: newId(),
      created_at: nowIso(),
      updated_at: nowIso(),
    }
    this.assertions.set(assertion.id, assertion)
    return assertion
  }

  getAssertion(id: string): OntologyAssertion | undefined {
    return this.assertions.get(id)
  }

  getAssertions(): OntologyAssertion[] {
    return Array.from(this.assertions.values())
  }

  getAssertionsForSubject(conceptId: string): OntologyAssertion[] {
    return this.getAssertions().filter((a) => a.subject_concept_id === conceptId)
  }

  getAssertionsForObject(conceptId: string): OntologyAssertion[] {
    return this.getAssertions().filter((a) => a.object_concept_id === conceptId)
  }

  getAssertionsForRelation(relationId: string): OntologyAssertion[] {
    return this.getAssertions().filter((a) => a.relation_id === relationId)
  }

  loadConcept(concept: OntologyConcept): void {
    this.concepts.set(concept.slug, OntologyConceptSchema.parse(concept))
  }

  loadRelation(relation: OntologyRelation): void {
    this.relations.set(relation.slug, OntologyRelationSchema.parse(relation))
  }

  loadAssertion(assertion: OntologyAssertion): void {
    this.assertions.set(assertion.id, OntologyAssertionSchema.parse(assertion))
  }

  loadFromSnapshot(snapshot: OntologySnapshot): void {
    for (const concept of snapshot.concepts) this.loadConcept(concept)
    for (const relation of snapshot.relations) this.loadRelation(relation)
    for (const assertion of snapshot.assertions) this.loadAssertion(assertion)
  }

  toJSON(): OntologySnapshot {
    return {
      concepts: this.getConcepts(),
      relations: this.getRelations(),
      assertions: this.getAssertions(),
    }
  }
}

// ── Default seed: FlowState ontology as of 2026-07-10 ─────────────────────

const CORE_ENTITY_CONCEPTS: Array<Omit<z.input<typeof OntologyConceptSchema>, 'id' | 'created_at' | 'updated_at'>> = [
  { slug: 'mycelium', name: 'Mycelium', kind: 'entity', description: 'Event-driven federated backbone' },
  { slug: 'principal', name: 'Principal', kind: 'entity', description: 'Federated actor (human, agent, system, integration)' },
  { slug: 'user', name: 'User', kind: 'entity', description: 'Human principal' },
  { slug: 'organization', name: 'Organization', kind: 'entity', description: 'Multi-tenant organization' },
  { slug: 'team', name: 'Team', kind: 'entity', description: 'Group of principals inside an organization' },
  { slug: 'task', name: 'Task', kind: 'entity', description: 'Unit of work' },
  { slug: 'project', name: 'Project', kind: 'entity', description: 'Container for tasks' },
  { slug: 'client', name: 'Client', kind: 'entity', description: 'CRM client' },
  { slug: 'deal', name: 'Deal', kind: 'entity', description: 'Sales opportunity' },
  { slug: 'invoice', name: 'Invoice', kind: 'entity', description: 'Accounts receivable document' },
  { slug: 'notification', name: 'Notification', kind: 'entity', description: 'Alert to a principal' },
  { slug: 'message', name: 'Message', kind: 'entity', description: 'Cross-channel message' },
  { slug: 'form', name: 'Form', kind: 'entity', description: 'Form engine form' },
  { slug: 'notebook', name: 'Notebook', kind: 'entity', description: 'Cortex knowledge notebook' },
  { slug: 'cortex', name: 'Cortex', kind: 'entity', description: 'In-app AI assistant/orchestrator' },
  { slug: 'skill', name: 'Skill', kind: 'entity', description: 'Procedural agent memory (SOP)' },
  { slug: 'memory', name: 'Memory', kind: 'entity', description: 'Declarative agent memory (fact)' },
  { slug: 'agent', name: 'Agent', kind: 'entity', description: 'Autonomous principal' },
  { slug: 'surface', name: 'Surface', kind: 'entity', description: 'Agent runtime surface' },
  { slug: 'concept', name: 'Concept', kind: 'entity', description: 'Ontology concept' },
  { slug: 'relation', name: 'Relation', kind: 'entity', description: 'Ontology relation' },
  { slug: 'assertion', name: 'Assertion', kind: 'entity', description: 'Ontology assertion (triple or property)' },
  { slug: 'system', name: 'System', kind: 'entity', description: 'System process or runtime' },
]

const CORE_RELATION_DEFINITIONS: Array<Omit<z.input<typeof OntologyRelationSchema>, 'id' | 'created_at' | 'updated_at'>> = [
  { slug: 'is_a', name: 'is a', source_concept_slugs: ['concept'], target_concept_slugs: ['concept'], cardinality: 'many-to-many', is_directed: true },
  { slug: 'has_part', name: 'has part', source_concept_slugs: ['concept'], target_concept_slugs: ['concept'], cardinality: 'one-to-many', is_directed: true },
  { slug: 'belongs_to', name: 'belongs to', source_concept_slugs: ['concept'], target_concept_slugs: ['concept'], cardinality: 'many-to-one', is_directed: true },
  { slug: 'owns', name: 'owns', source_concept_slugs: ['concept'], target_concept_slugs: ['concept'], cardinality: 'one-to-many', is_directed: true },
  { slug: 'created_by', name: 'created by', source_concept_slugs: ['concept'], target_concept_slugs: ['principal'], cardinality: 'many-to-one', is_directed: true },
  { slug: 'triggers', name: 'triggers', source_concept_slugs: ['event'], target_concept_slugs: ['concept'], cardinality: 'one-to-many', is_directed: true },
  { slug: 'depends_on', name: 'depends on', source_concept_slugs: ['concept'], target_concept_slugs: ['concept'], cardinality: 'many-to-many', is_directed: true },
  { slug: 'has_domain', name: 'has domain', source_concept_slugs: ['concept'], target_concept_slugs: ['domain'], cardinality: 'many-to-one', is_directed: true },
  { slug: 'has_scope', name: 'has scope', source_concept_slugs: ['concept'], target_concept_slugs: ['scope'], cardinality: 'many-to-one', is_directed: true },
  { slug: 'has_skill', name: 'has skill', source_concept_slugs: ['agent'], target_concept_slugs: ['skill'], cardinality: 'one-to-many', is_directed: true },
  { slug: 'has_memory', name: 'has memory', source_concept_slugs: ['principal'], target_concept_slugs: ['memory'], cardinality: 'one-to-many', is_directed: true },
  { slug: 'has_surface', name: 'has surface', source_concept_slugs: ['agent'], target_concept_slugs: ['surface'], cardinality: 'one-to-many', is_directed: true },
  { slug: 'asserts', name: 'asserts', source_concept_slugs: ['principal'], target_concept_slugs: ['assertion'], cardinality: 'one-to-many', is_directed: true },
]

function idOf(registry: OntologyRegistry, slug: string): string {
  const concept = registry.getConcept(slug)
  if (!concept) {
    throw new Error(`Ontology seed: missing concept '${slug}'`)
  }
  return concept.id
}

function relIdOf(registry: OntologyRegistry, slug: string): string {
  const relation = registry.getRelation(slug)
  if (!relation) {
    throw new Error(`Ontology seed: missing relation '${slug}'`)
  }
  return relation.id
}

/**
 * Create a pre-seeded registry that reflects the current FlowState
 * infrastructure and Mycelium backbone.
 */
export function createDefaultOntology(): OntologyRegistry {
  const registry = new OntologyRegistry()

  // Core entities
  for (const concept of CORE_ENTITY_CONCEPTS) {
    registry.defineConcept(concept)
  }

  // Backbone domains
  for (const domain of EventDomainSchema.options) {
    registry.defineConcept({
      slug: `domain:${domain}`,
      name: `${domain} (domain)`,
      kind: 'domain',
      domain,
    })
  }

  // Memory scope kinds
  for (const scope of MemoryScopeKindSchema.options) {
    registry.defineConcept({
      slug: `scope:${scope}`,
      name: `${scope} (scope)`,
      kind: 'scope',
    })
  }

  // Agent surfaces
  for (const surface of Object.values(AgentSurfaces)) {
    registry.defineConcept({
      slug: `surface:${surface}`,
      name: `${surface} (surface)`,
      kind: 'surface',
    })
  }

  // Agent CLIs
  for (const cli of DEFAULT_AGENT_CLIS) {
    registry.defineConcept({
      slug: `cli:${cli.id}`,
      name: `${cli.name} (CLI)`,
      kind: 'surface',
      metadata: { category: cli.category },
    })
  }

  // Relations
  for (const relation of CORE_RELATION_DEFINITIONS) {
    registry.defineRelation(relation)
  }

  // Seed assertions
  const assertions: Array<[string, string, string | undefined, string | undefined]> = [
    ['mycelium', 'is_a', 'system', undefined],
    ['cortex', 'is_a', 'agent', undefined],
    ['user', 'is_a', 'principal', undefined],
    ['agent', 'is_a', 'principal', undefined],
    ['skill', 'is_a', 'concept', undefined],
    ['memory', 'is_a', 'concept', undefined],
    ['organization', 'has_part', 'team', undefined],
    ['team', 'has_part', 'user', undefined],
    ['principal', 'has_memory', 'memory', undefined],
    ['agent', 'has_skill', 'skill', undefined],
    ['task', 'has_domain', 'domain:task', undefined],
    ['project', 'has_domain', 'domain:task', undefined],
    ['client', 'has_domain', 'domain:crm', undefined],
    ['deal', 'has_domain', 'domain:sales', undefined],
    ['invoice', 'has_domain', 'domain:accounting', undefined],
    ['notification', 'has_domain', 'domain:notification', undefined],
    ['message', 'has_domain', 'domain:messaging', undefined],
    ['form', 'has_domain', 'domain:forms', undefined],
    ['notebook', 'has_domain', 'domain:notebook', undefined],
    ['memory', 'has_domain', 'domain:memory', undefined],
    ['skill', 'has_domain', 'domain:memory', undefined],
    ['principal', 'has_scope', 'scope:organization', undefined],
    ['cortex', 'has_surface', 'surface:cortex', undefined],
    ['mycelium', 'depends_on', 'domain:system', undefined],
    ['cortex', 'depends_on', 'memory', undefined],
    ['agent', 'depends_on', 'skill', undefined],
    ['task', 'depends_on', 'project', undefined],
    ['deal', 'depends_on', 'client', undefined],
    ['invoice', 'depends_on', 'deal', undefined],
  ]

  for (const [subjectSlug, relationSlug, objectSlug, value] of assertions) {
    registry.assert({
      subject_concept_id: idOf(registry, subjectSlug),
      relation_id: relIdOf(registry, relationSlug),
      object_concept_id: objectSlug ? idOf(registry, objectSlug) : undefined,
      value,
      confidence: 1.0,
      evidence: 'seeded from FlowState ontology',
    })
  }

  return registry
}

/**
 * Global singleton registry.
 * Safe to import and use from any module.
 */
export const flowstateOntology = createDefaultOntology()
